/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be.ignitor

import io.peekandpoke.klang.audio_be.AudioBuffer

/**
 * Wraps a source [Ignitor] with a pre-computed pitch modulation signal.
 *
 * At runtime, generates the [mod] Ignitor (which outputs ratio-space values, 1.0 = no change),
 * combines with any `ctx.phaseMod` already set (an enclosing [ModApplyingIgnitor]'s; a voice's tree root carries
 * none since pitch pipeline step 5), and calls [inner] with the combined modulation set on the context.
 *
 * This class is designed to sit **inside** a [MemoizingIgnitor] boundary. On cache hit, neither
 * `mod.generate` nor the `ctx.phaseMod` mutation occurs — the memoised output is returned directly.
 * This eliminates the phase-mod–memoisation conflict: each fork of a shared source gets its own
 * `Memoized(ModApplyingIgnitor(...))`, so different mods on the same source don't interfere.
 *
 * @param inner the source oscillator (reads `ctx.phaseMod` per sample as today)
 * @param mod ratio-space modulation signal (1.0 = no change, >1.0 = higher pitch, <1.0 = lower)
 */
internal class ModApplyingIgnitor(val inner: Ignitor, val mod: Ignitor) : Ignitor {

    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        ctx.scratchBuffers.use { modBuf ->
            mod.generate(modBuf, freqHz, ctx)

            ctx.scratchBuffers.useDouble { ratioArray ->
                val existing = ctx.phaseMod
                val end = ctx.windowEnd

                if (existing != null) {
                    for (i in ctx.offset until end) {
                        ratioArray[i] = modBuf[i] * existing[i]
                    }
                } else {
                    for (i in ctx.offset until end) {
                        ratioArray[i] = modBuf[i]
                    }
                }

                val savedMod = ctx.phaseMod
                ctx.phaseMod = ratioArray
                inner.generate(buffer, freqHz, ctx)
                ctx.phaseMod = savedMod
            }
        }
    }
}
